import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface TenantSpace {
  name: string;
  slug: string;
  description: string;
}

export interface TenantConfig {
  tenant_id: string;
  tenant_name: string;
  tenant_type: 'single_space' | 'multi_space';
  primary_color: string;
  secondary_color: string;
  logo_url?: string;
  support_email: string;
  spaces: TenantSpace[];
  available_tags: string[];
}

interface TenantContextType {
  tenantConfig: TenantConfig | null;
  isLoading: boolean;
  error: string | null;
  refreshConfig: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTenantConfig = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // For now, return a default config based on environment
      // TODO: Replace with actual API call when backend is connected
      const tenantId = process.env.REACT_APP_TENANT_ID || 'severance';
      
      if (tenantId === 'evanston') {
        setTenantConfig({
          tenant_id: 'evanston',
          tenant_name: 'Evanston PAW',
          tenant_type: 'multi_space',
          primary_color: '#6B46C1',
          secondary_color: '#F59E0B',
          support_email: 'evanston.support@mpactcollective.com',
          spaces: [
            {
              name: 'Police/Fire Station',
              slug: 'police-fire-station',
              description: 'Ideas for the redevelopment of the police and fire station facility'
            },
            {
              name: 'Noyes Cultural Arts Center',
              slug: 'noyes-cultural-center',
              description: 'Ideas for enhancing the Noyes Cultural Arts Center'
            },
            {
              name: 'Civic Center (Old City Hall)',
              slug: 'civic-center',
              description: 'Ideas for the transformation of the old city hall into a civic center'
            }
          ],
          available_tags: [
            'Public Safety',
            'Arts & Culture',
            'Community Services',
            'Government Operations',
            'Public Spaces',
            'Accessibility',
            'Sustainability',
            'Economic Development',
            'Education',
            'Recreation'
          ]
        });
      } else {
        // Default to Severance
        setTenantConfig({
          tenant_id: 'severance',
          tenant_name: 'Severance Town Center Project',
          tenant_type: 'single_space',
          primary_color: '#3c5770',
          secondary_color: '#d4ad43',
          support_email: 'severance.support@mpactcollective.com',
          spaces: [],
          available_tags: [
            'Residential',
            'Retail',
            'Entertainment',
            'Office',
            'Hospitality',
            'Medical',
            'Educational',
            'Recreational',
            'Sustainable/Regenerative'
          ]
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenant configuration');
      console.error('Tenant config loading error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshConfig = async () => {
    await loadTenantConfig();
  };

  useEffect(() => {
    loadTenantConfig();
  }, []);

  const value: TenantContextType = {
    tenantConfig,
    isLoading,
    error,
    refreshConfig,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}; 